import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RotateCcw, RotateCw, X, Trash2, Ruler, Minus, Plus, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
import useStore from '../store/useStore';
import { formatDimension } from '../lib/utils';

export default function RotationToolbar() {
  const {
    placedElements,
    selectedPlacedId,
    setSelectedPlacedId,
    updatePlacedElement,
    removePlacedElement,
    categories,
    elements: catalogElements
  } = useStore();

  const selectedElement = placedElements.find(el => el.id === selectedPlacedId);
  const isMonument = categories?.find(c => c.id === selectedElement?.category_id)?.name === 'monument';
  const isPedestal = selectedElement?.model_type === 'pedestal' || categories?.find(c => c.id === selectedElement?.category_id)?.name === 'pedestal';
  const isEngravingText = selectedElement?.model_type === 'engraving_text';
  const isEngraving = selectedElement?.model_type === 'engraving_text' || selectedElement?.model_type === 'engraving_portrait';
  const depthMin = isMonument ? 0.07 : 0.1;
  const depthStep = isMonument ? 0.01 : 0.1;
  // Производственный минимум семейной (горизонтальной) стелы — 120×60×7,
  // меньше не изготавливают (не помещаются два портрета и две гравировки ФИО).
  const FAMILY_STELE_MIN = { width: 1.2, height: 0.6, depth: 0.07 };
  const isFamilyStele = selectedElement?.name === 'family_stele';
  const minForParam = (param) =>
    (isFamilyStele && FAMILY_STELE_MIN[param]) || (param === 'depth' ? depthMin : 0.1);
  const [showParams, setShowParams] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'width' | 'depth' | 'height' | null
  const [editValue, setEditValue] = useState('');

  // Тумба в 3D рисуется горизонтально: при height > width размеры в сцене меняются местами (длина ↔ высота).
  // Чтобы «Ширина»/«Высота» в тулбаре соответствовали тому, что видно в 3D, используем те же эффективные размеры.
  const getPedestalDisplayParams = (el) => {
    if (!el) return { width: 1, depth: 1, height: 1 };
    const w = el.width ?? 0.25, h = el.height ?? 0.5, d = el.depth ?? 0.25;
    const swapped = h > w;
    return {
      width: swapped ? h : w,   // горизонтальная длина в 3D
      depth: d,
      height: swapped ? w : h  // вертикальная высота в 3D
    };
  };
  const getPedestalStoreKey = (el, param) => {
    if (!el) return param;
    const w = el.width ?? 0.25, h = el.height ?? 0.5;
    const swapped = h > w;
    if (param === 'width') return swapped ? 'height' : 'width';
    if (param === 'height') return swapped ? 'width' : 'height';
    return 'depth';
  };
  
  // Check if element is GLB model
  const isGLBModel = selectedElement?.model_url && 
    (selectedElement.model_url.endsWith('.glb') || selectedElement.model_url.endsWith('.gltf'));
  
  // Local state for parameters editing
  const [params, setParams] = useState({
    width: 1,
    height: 1,
    depth: 1,
    scale: 1 // For GLB models - single scale parameter
  });

  // Update local params when selected element changes
  useEffect(() => {
    if (selectedElement) {
      if (isPedestal) {
        const disp = getPedestalDisplayParams(selectedElement);
        setParams({
          width: disp.width,
          height: disp.height,
          depth: disp.depth,
          scale: Math.max(disp.width, disp.height, disp.depth)
        });
      } else {
        setParams({
          width: selectedElement.width || 1,
          height: selectedElement.height || 1,
          depth: selectedElement.depth || 1,
          scale: Math.max(selectedElement.width || 1, selectedElement.height || 1, selectedElement.depth || 1)
        });
      }
    }
  }, [selectedElement?.id, selectedElement?.width, selectedElement?.height, selectedElement?.depth, isPedestal]);

  // Close params panel when a DIFFERENT element is selected
  useEffect(() => {
    setShowParams(false);
  }, [selectedElement?.id]);

  // Debounced update to prevent lag
  const debouncedUpdate = useCallback((updates) => {
    updatePlacedElement(selectedPlacedId, updates);
  }, [selectedPlacedId, updatePlacedElement]);

  if (!selectedElement) return null;

  const handleRotateLeft = () => {
    updatePlacedElement(selectedPlacedId, {
      rotation: ((selectedElement.rotation || 0) - 90 + 360) % 360
    });
  };

  const handleRotateRight = () => {
    updatePlacedElement(selectedPlacedId, {
      rotation: ((selectedElement.rotation || 0) + 90) % 360
    });
  };

  const ENGRAVING_STEP = 0.03; // 3cm per arrow click
  const handleEngravingMove = (dir) => {
    const offX = selectedElement.engravingOffsetX || 0;
    const offY = selectedElement.engravingOffsetY || 0;
    if (dir === 'up')    updatePlacedElement(selectedPlacedId, { engravingOffsetY: offY + ENGRAVING_STEP });
    if (dir === 'down')  updatePlacedElement(selectedPlacedId, { engravingOffsetY: offY - ENGRAVING_STEP });
    if (dir === 'left')  updatePlacedElement(selectedPlacedId, { engravingOffsetX: offX - ENGRAVING_STEP });
    if (dir === 'right') updatePlacedElement(selectedPlacedId, { engravingOffsetX: offX + ENGRAVING_STEP });
  };

  const handleDelete = () => {
    removePlacedElement(selectedPlacedId);
    setSelectedPlacedId(null);
  };

  const handleClose = () => {
    setSelectedPlacedId(null);
  };

  // For primitive objects - change individual dimensions (для тумбы — обновляем тот ключ стора, который соответствует отображаемому размеру в 3D)
  const handleParamChange = (param, value) => {
    const numValue = parseFloat(value);
    const minVal = minForParam(param);
    const fallback = minVal;
    const clampedValue = Number.isNaN(numValue) ? fallback : Math.max(minVal, Math.min(10, numValue));
    
    setParams(prev => ({ ...prev, [param]: clampedValue }));
    const storeKey = isPedestal ? getPedestalStoreKey(selectedElement, param) : param;
    debouncedUpdate({ [storeKey]: clampedValue });
  };

  const incrementParam = (param, delta) => {
    const minVal = minForParam(param);
    const newValue = Math.max(minVal, Math.min(10, (params[param] || 1) + delta));
    const decimals = param === 'depth' && isMonument ? 2 : 1;
    const rounded = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    setParams(prev => ({ ...prev, [param]: rounded }));
    const storeKey = isPedestal ? getPedestalStoreKey(selectedElement, param) : param;
    debouncedUpdate({ [storeKey]: rounded });
  };

  // Parse user input: accepts "60 см", "1.2 м", "1,2", "60", plain numbers
  // Plain numbers are interpreted in the same unit that was displayed (cm or m)
  const parseInputValue = (raw, field) => {
    const s = raw.replace(',', '.').replace(/\s+/g, '');
    const cmMatch = s.match(/^([\d.]+)(?:см|cm)$/i);
    if (cmMatch) return parseFloat(cmMatch[1]) / 100;
    const mMatch = s.match(/^([\d.]+)(?:м|m)$/i);
    if (mMatch) return parseFloat(mMatch[1]);
    const num = parseFloat(s);
    if (Number.isNaN(num) || num <= 0) return null;
    // If original value was shown in cm, interpret as cm; otherwise as meters
    const wasInCm = params[field] < 1;
    return wasInCm ? num / 100 : num;
  };

  const handleFieldFocus = (field) => {
    setEditingField(field);
    const val = params[field];
    setEditValue(val < 1 ? String(Math.round(val * 100)) : String(parseFloat(val.toFixed(2))));
  };

  const handleFieldBlur = (field) => {
    const parsed = parseInputValue(editValue, field);
    if (parsed !== null) {
      handleParamChange(field, parsed);
    }
    setEditingField(null);
    setEditValue('');
  };

  const handleFieldKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditingField(null);
      setEditValue('');
    }
  };

  // For GLB models - change scale (affects all dimensions proportionally)
  const handleScaleChange = (value) => {
    const numValue = parseFloat(value) || 0.1;
    const clampedValue = Math.max(0.1, Math.min(10, numValue));
    
    setParams(prev => ({ 
      ...prev, 
      scale: clampedValue,
      width: clampedValue,
      height: clampedValue,
      depth: clampedValue
    }));
    debouncedUpdate({ 
      width: clampedValue, 
      height: clampedValue, 
      depth: clampedValue 
    });
  };

  const incrementScale = (delta) => {
    const newValue = Math.max(0.1, Math.min(10, (params.scale || 1) + delta));
    const rounded = Math.round(newValue * 10) / 10;
    setParams(prev => ({ 
      ...prev, 
      scale: rounded,
      width: rounded,
      height: rounded,
      depth: rounded
    }));
    debouncedUpdate({ 
      width: rounded, 
      height: rounded, 
      depth: rounded 
    });
  };

  // Calculate price display based on price_unit
  const calculatePriceInfo = () => {
    const priceUnit = selectedElement.price_unit || 'piece';
    const basePrice = selectedElement.price || 0;
    
    if (priceUnit === 'cubic_meter') {
      // Полый цветник: объём только камня (3 балки). Только если явно цветник и не rectangle
      const isPShape = selectedElement?.model_type === 'flowerbed' && selectedElement?.flowerbed_shape != null && selectedElement?.flowerbed_shape !== 'rectangle';
      const volume = isPShape
        ? 0.08 * params.height * (2 * params.depth + params.width)
        : params.width * params.height * params.depth;
      return {
        label: `${volume.toFixed(2)} м³`,
        price: basePrice * volume
      };
    } else if (priceUnit === 'linear_meter') {
      return {
        label: `${params.width.toFixed(2)} п.м.`,
        price: basePrice * params.width
      };
    } else if (priceUnit === 'sqmeter') {
      const area = params.width * params.depth;
      return {
        label: `${area.toFixed(2)} м²`,
        price: basePrice * area
      };
    } else if (priceUnit === 'sqmeter_wh') {
      const origEl = catalogElements?.find(e => e.id === (selectedElement.element_id || selectedElement.id));
      const defaultArea = origEl ? (origEl.width || 1) * (origEl.height || 1) : 1;
      const currentArea = params.width * params.height;
      return {
        label: `${Math.round(params.width * 100)} × ${Math.round(params.height * 100)} см`,
        price: basePrice * (currentArea / defaultArea)
      };
    } else {
      return {
        label: '1 шт',
        price: basePrice
      };
    }
  };

  const priceInfo = calculatePriceInfo();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(price) + ' ₸';
  };

  return (
    <div 
      className="pointer-events-auto absolute bottom-16 sm:bottom-24 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2 w-[calc(100%-1rem)] sm:w-auto max-w-md sm:max-w-none"
      data-testid="rotation-toolbar"
    >
      {/* Parameters panel */}
      {showParams && (
        <div
          className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl sm:rounded-2xl shadow-xl p-2 sm:p-3 w-full sm:w-auto animate-in slide-in-from-bottom-2 duration-200"
          data-testid="params-panel"
        >
          {isEngraving && !isEngravingText ? (
            /* Engraving portrait — proportional size control */
            <div className="flex flex-col gap-2 w-full sm:w-64">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium text-center">Размер гравировки</p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="ghost"
                  className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                  onClick={() => {
                    const w = Math.max(0.05, (selectedElement.width || 0.25) - 0.01);
                    const ratio = (selectedElement.height || 0.35) / (selectedElement.width || 0.25);
                    debouncedUpdate({ width: +w.toFixed(3), height: +(w * ratio).toFixed(3) });
                  }}>
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium text-slate-700 w-16 text-center">
                  {Math.round((selectedElement.width || 0.25) * 100)} × {Math.round((selectedElement.height || 0.35) * 100)} см
                </span>
                <Button variant="ghost"
                  className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                  onClick={() => {
                    const w = (selectedElement.width || 0.25) + 0.01;
                    const ratio = (selectedElement.height || 0.35) / (selectedElement.width || 0.25);
                    debouncedUpdate({ width: +w.toFixed(3), height: +(w * ratio).toFixed(3) });
                  }}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-center gap-2 pt-1 border-t border-slate-100">
                <span className="text-[10px] text-slate-500">{priceInfo.label}:</span>
                <span className="text-sm font-bold text-primary">{formatPrice(priceInfo.price)}</span>
              </div>
            </div>
          ) : isEngravingText ? (
            /* Engraving text — FIO fields */
            <div className="flex flex-col gap-2 w-full sm:w-72">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium text-center">Текст гравировки</p>
              {[
                { label: 'Фамилия', field: 'lastName' },
                { label: 'Имя', field: 'firstName' },
                { label: 'Отчество', field: 'patronymic' },
              ].map(({ label, field }) => (
                <div key={field} className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 w-20 shrink-0">{label}</span>
                  <Input
                    className="h-7 text-sm flex-1 min-w-0 px-2"
                    placeholder={label}
                    value={selectedElement[field] || ''}
                    onChange={(e) => debouncedUpdate({ [field]: e.target.value })}
                  />
                </div>
              ))}
              <div className="flex items-center justify-center gap-2 pt-1 border-t border-slate-100">
                <span className="text-[10px] text-slate-500">{priceInfo.label}:</span>
                <span className="text-sm font-bold text-primary">{formatPrice(priceInfo.price)}</span>
              </div>
            </div>
          ) : isGLBModel ? (
            /* GLB Model - single scale parameter */
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <div className="flex flex-col items-center gap-0.5">
                <Label className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wide">Размер</Label>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                    onClick={() => incrementScale(-0.1)}
                  >
                    <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10"
                    value={params.scale.toFixed(1)}
                    onChange={(e) => handleScaleChange(e.target.value)}
                    className="w-16 sm:w-20 h-8 sm:h-9 text-center text-sm sm:text-base font-medium px-1"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                    onClick={() => incrementScale(0.1)}
                  >
                    <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </div>
                <span className="text-[10px] text-slate-400">м</span>
              </div>

              {/* Price info */}
              <div className="flex flex-col items-center gap-1 sm:pl-3 sm:border-l border-slate-200">
                <Label className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wide">Стоимость</Label>
                <div className="text-sm font-bold text-primary">
                  {formatPrice(priceInfo.price)}
                </div>
                <span className="text-[10px] text-slate-400">{priceInfo.label}</span>
              </div>
            </div>
          ) : (
            /* Primitive objects - three dimensions */
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center justify-between sm:justify-start gap-1 sm:gap-4">
                {/* Width */}
                <div className="flex flex-col items-center gap-0.5 flex-1 sm:flex-none">
                  <Label className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wide">Ширина</Label>
                  <div className="flex items-center gap-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                      onClick={() => incrementParam('width', -0.1)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <Input
                      value={editingField === 'width' ? editValue : formatDimension(params.width)}
                      onChange={(e) => setEditValue(e.target.value)}
                      onFocus={() => handleFieldFocus('width')}
                      onBlur={() => handleFieldBlur('width')}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'width')}
                      className="w-12 sm:w-14 h-7 sm:h-8 text-center text-xs sm:text-sm font-medium px-1"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                      onClick={() => incrementParam('width', 0.1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Depth */}
                <div className="flex flex-col items-center gap-0.5 flex-1 sm:flex-none">
                  <Label className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wide">Толщина</Label>
                  <div className="flex items-center gap-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                      onClick={() => incrementParam('depth', -depthStep)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <Input
                      value={editingField === 'depth' ? editValue : formatDimension(params.depth)}
                      onChange={(e) => setEditValue(e.target.value)}
                      onFocus={() => handleFieldFocus('depth')}
                      onBlur={() => handleFieldBlur('depth')}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'depth')}
                      className="w-12 sm:w-14 h-7 sm:h-8 text-center text-xs sm:text-sm font-medium px-1"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                      onClick={() => incrementParam('depth', depthStep)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Height */}
                <div className="flex flex-col items-center gap-0.5 flex-1 sm:flex-none">
                  <Label className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wide">Высота</Label>
                  <div className="flex items-center gap-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                      onClick={() => incrementParam('height', -0.1)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <Input
                      value={editingField === 'height' ? editValue : formatDimension(params.height)}
                      onChange={(e) => setEditValue(e.target.value)}
                      onFocus={() => handleFieldFocus('height')}
                      onBlur={() => handleFieldBlur('height')}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'height')}
                      className="w-12 sm:w-14 h-7 sm:h-8 text-center text-xs sm:text-sm font-medium px-1"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-full bg-slate-100 hover:bg-slate-200"
                      onClick={() => incrementParam('height', 0.1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Price info - desktop */}
              <div className="hidden sm:flex flex-col items-center gap-1 pl-3 border-l border-slate-200">
                <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Стоимость</Label>
                <div className="text-sm font-bold text-primary">
                  {formatPrice(priceInfo.price)}
                </div>
                <span className="text-[10px] text-slate-400">{priceInfo.label}</span>
              </div>
            </div>
          )}
          
          {/* Mobile price row - only for primitives (not GLB, not engraving text) */}
          {!isGLBModel && !isEngravingText && (
            <div className="flex sm:hidden items-center justify-center gap-2 mt-2 pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-500">{priceInfo.label}:</span>
              <span className="text-sm font-bold text-primary">{formatPrice(priceInfo.price)}</span>
            </div>
          )}
        </div>
      )}

      {/* Main rotation toolbar */}
      <div className="flex items-center gap-1.5 sm:gap-2 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-full shadow-xl px-2 sm:px-3 py-1.5 sm:py-2">
        {/* Element name */}
        <span className="text-xs sm:text-sm font-medium text-slate-700 px-1 sm:px-2 max-w-[100px] sm:max-w-[150px] truncate">
          {selectedElement.name_ru}
        </span>
        
        {/* Toggle parameters (ruler icon) */}
        <Button
          onClick={() => setShowParams(!showParams)}
          className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full transition-all ${showParams ? 'bg-primary text-white hover:bg-primary/90' : 'bg-primary/10 text-primary ring-2 ring-primary/40 hover:bg-primary/20'}`}
          variant="ghost"
          data-testid="toggle-params-btn"
        >
          <Ruler className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>
        
        {/* Engraving: arrow buttons; others: rotate buttons */}
        {isEngraving ? (
          <>
            <Button onClick={() => handleEngravingMove('up')}
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
              variant="ghost" title="Вверх">
              <ArrowUp className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button onClick={() => handleEngravingMove('down')}
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
              variant="ghost" title="Вниз">
              <ArrowDown className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button onClick={() => handleEngravingMove('left')}
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
              variant="ghost" title="Влево">
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button onClick={() => handleEngravingMove('right')}
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
              variant="ghost" title="Вправо">
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleRotateLeft}
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
              variant="ghost" data-testid="rotate-left-btn">
              <RotateCcw className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button onClick={handleRotateRight}
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
              variant="ghost" data-testid="rotate-right-btn">
              <RotateCw className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          </>
        )}
        
        {/* Delete */}
        <Button
          onClick={handleDelete}
          className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-red-100 hover:bg-red-200 text-red-600"
          variant="ghost"
          data-testid="delete-element-btn"
        >
          <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>
        
        {/* Close */}
        <Button
          onClick={handleClose}
          className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500"
          variant="ghost"
          data-testid="close-toolbar-btn"
        >
          <X className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>
      </div>
    </div>
  );
}
